import type { CursorPage } from '../../lib/use-cursor-list';
import { api } from '../../lib/api-client';

export type UnitStockStatus =
  | 'INCOMING'
  | 'IN_STOCK'
  | 'RESERVED'
  | 'ALLOCATED'
  | 'REGISTRATION'
  | 'READY_DELIVERY'
  | 'DELIVERED'
  | 'WORKSHOP'
  | 'TEST_UNIT'
  | 'TRANSFER_OUT'
  | 'WRITTEN_OFF';

export interface Unit {
  uid: string;
  chassisNo: string;
  engineNo: string | null;
  model: string | null;
  colour: string | null;
  modelYear: number | null;
  stockStatus: UnitStockStatus;
  location: string | null;
  branchCode: string | null;
  plateNo: string | null;
  allocatedCustomerUid: string | null;
  allocatedCustomerName: string | null;
  /** The sale AutoCount records for a bike sold before the DMS. */
  autocountSale: { docNo: string; soldOn: string; debtorCode: string; customer: { uid: string; name: string } | null } | null;
  isTestUnit: boolean;
  receivedAt: string | null;
  /** Days on the floor while still unsold — the aging number NKS asked for. */
  ageDays: number | null;
  createdAt: string;
}

export interface UnitPage {
  items: Unit[];
  nextCursor: string | null;
  hasMore: boolean;
}

export function getUnit(uid: string): Promise<Unit> {
  return api<Unit>(`/units/${uid}`);
}

export function listUnits(params: {
  q?: string;
  stockStatus?: UnitStockStatus;
  available?: boolean;
  cursor?: string;
}): Promise<UnitPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.stockStatus !== undefined) search.set('stockStatus', params.stockStatus);
  if (params.available === true) search.set('available', 'true');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<UnitPage>(`/units${qs.length > 0 ? `?${qs}` : ''}`);
}

// ─── registration ──────────────────────────────────────────────────────

export type RegistrationCaseStatus = 'OPEN' | 'COMPLETE' | 'CANCELLED';
export type RegistrationStageCode = 'AP' | 'PUSPAKOM' | 'JPJ';
export type RegistrationStageStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'BLOCKED';
export type ChecklistItemStatus = 'PENDING' | 'RECEIVED' | 'WAIVED' | 'NOT_APPLICABLE';

export interface StageChecklistItem {
  uid: string;
  code: string;
  label: string;
  isMandatory: boolean;
  status: ChecklistItemStatus;
  note: string | null;
}

export interface RegistrationStage {
  uid: string;
  stage: RegistrationStageCode;
  label: string;
  status: RegistrationStageStatus;
  ownerUid: string | null;
  ownerName: string | null;
  targetDate: string | null;
  startedAt: string | null;
  completedAt: string | null;
  referenceNo: string | null;
  blockedReason: string | null;
  /** Days late, or null. A BLOCKED stage still ages — that is the point. */
  daysOverdue: number | null;
  daysInStage: number | null;
  targetDays: number;
  checklist: {
    uid: string;
    label: string;
    items: StageChecklistItem[];
    outstanding: string[];
    isComplete: boolean;
  } | null;
}

export interface RegistrationCase {
  uid: string;
  caseNo: string;
  status: RegistrationCaseStatus;
  unitUid: string;
  chassisNo: string;
  model: string | null;
  plateNo: string | null;
  customerUid: string | null;
  customerName: string | null;
  bookingUid: string | null;
  bookingNo: string | null;
  ownerUid: string | null;
  ownerName: string | null;
  openedAt: string;
  closedAt: string | null;
  currentStage: RegistrationStageCode | null;
  worstOverdueDays: number | null;
  stages: RegistrationStage[];
  branchCode: string | null;
}

export interface RegistrationPage {
  items: RegistrationCase[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface RegistrationSummary {
  gates: { stage: RegistrationStageCode; waiting: number; overdue: number }[];
}

export function registrationSummary(): Promise<RegistrationSummary> {
  return api<RegistrationSummary>('/registration-cases/summary');
}

export function listRegistrationCases(params: {
  q?: string;
  status?: RegistrationCaseStatus;
  stage?: RegistrationStageCode;
  overdue?: boolean;
  cursor?: string;
}): Promise<RegistrationPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.stage !== undefined) search.set('stage', params.stage);
  if (params.overdue === true) search.set('overdue', 'true');
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<RegistrationPage>(`/registration-cases${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getRegistrationCase(uid: string): Promise<RegistrationCase> {
  return api<RegistrationCase>(`/registration-cases/${uid}`);
}

export function openRegistrationCase(input: {
  unitUid: string;
  ownerUid?: string;
}): Promise<RegistrationCase> {
  return api<RegistrationCase>('/registration-cases', { method: 'POST', body: input });
}

export function updateStage(
  stageUid: string,
  input: {
    status?: RegistrationStageStatus;
    ownerUid?: string;
    referenceNo?: string;
    reason?: string;
  },
): Promise<RegistrationCase> {
  return api<RegistrationCase>(`/registration-cases/stages/${stageUid}`, {
    method: 'PATCH',
    body: input,
  });
}

// ─── delivery ──────────────────────────────────────────────────────────

export type DeliveryStatus = 'SCHEDULED' | 'READY' | 'DELIVERED' | 'CANCELLED';
export type PdiResult = 'PASS' | 'FAIL' | 'NA';
export type HandoverAckType = 'SIGNATURE' | 'OTP' | 'PHOTO';

export interface PdiItem {
  uid: string;
  code: string;
  label: string;
  result: PdiResult | null;
  note: string | null;
  checkedAt: string | null;
}

export interface Delivery {
  uid: string;
  doNo: string;
  status: DeliveryStatus;
  unitUid: string;
  chassisNo: string;
  plateNo: string | null;
  model: string | null;
  customerUid: string;
  customerName: string;
  bookingUid: string | null;
  bookingNo: string | null;
  scheduledAt: string | null;
  deliveredAt: string | null;
  pdiCompletedAt: string | null;
  handoverAckType: string | null;
  handoverAckRef: string | null;
  odometer: number | null;
  notes: string | null;
  pdiItems: PdiItem[];
  pdiOutstanding: string[];
  pdiFailed: string[];
  branchCode: string | null;
  createdAt: string;
}

export interface DeliveryPage {
  items: Delivery[];
  nextCursor: string | null;
  hasMore: boolean;
}

export function listDeliveries(params: {
  q?: string;
  status?: DeliveryStatus;
  unitUid?: string;
  cursor?: string;
}): Promise<DeliveryPage> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) search.set('q', params.q);
  if (params.status !== undefined) search.set('status', params.status);
  if (params.unitUid !== undefined) search.set('unitUid', params.unitUid);
  if (params.cursor !== undefined && params.cursor.length > 0) search.set('cursor', params.cursor);
  const qs = search.toString();
  return api<DeliveryPage>(`/deliveries${qs.length > 0 ? `?${qs}` : ''}`);
}

export function getDelivery(uid: string): Promise<Delivery> {
  return api<Delivery>(`/deliveries/${uid}`);
}

export function scheduleDelivery(input: {
  unitUid: string;
  scheduledAt?: string;
  notes?: string;
}): Promise<Delivery> {
  return api<Delivery>('/deliveries', { method: 'POST', body: input });
}

export function recordPdi(itemUid: string, input: { result: PdiResult; note?: string }): Promise<Delivery> {
  return api<Delivery>(`/deliveries/pdi-items/${itemUid}`, { method: 'PATCH', body: input });
}

export function handover(
  uid: string,
  input: { handoverAckType: HandoverAckType; handoverAckRef: string; odometer?: number },
): Promise<Delivery> {
  return api<Delivery>(`/deliveries/${uid}/transitions/handover`, { method: 'POST', body: input });
}

/** Shared with the financing checklist — one endpoint, one shape. */
export function updateChecklistItem(
  itemUid: string,
  input: { status: ChecklistItemStatus; note?: string },
): Promise<unknown> {
  return api<unknown>(`/financing/checklist-items/${itemUid}`, { method: 'PATCH', body: input });
}

export interface UnitLocation {
  uid: string;
  code: string;
  name: string;
  type: string;
}

export function listLocations(): Promise<UnitLocation[]> {
  return api<UnitLocation[]>('/units/locations');
}

export function moveUnit(
  uid: string,
  input: {
    toLocationUid: string;
    reason: string;
    note?: string;
    /** BARCODE when the chassis came off a scanner, MANUAL when typed. */
    source?: 'MANUAL' | 'BARCODE';
  },
): Promise<Unit> {
  return api<Unit>(`/units/${uid}/transitions/move`, { method: 'POST', body: input });
}

export function findUnitByChassis(chassisNo: string): Promise<Unit> {
  return api<Unit>(`/units/by-chassis/${encodeURIComponent(chassisNo)}`);
}

export function changeUnitStatus(
  uid: string,
  input: { stockStatus: UnitStockStatus; reason?: string },
): Promise<Unit> {
  return api<Unit>(`/units/${uid}/transitions/status`, { method: 'POST', body: input });
}

// ── AutoCount model mapping (Q85) ────────────────────────────────────────────

export interface UnmappedGroup {
  itemCode: string;
  unitCount: number;
  sampleChassis: string[];
  colours: string[];
  modelYears: string[];
}

export interface ModelOption {
  uid: string;
  label: string;
}

export interface ExistingMapping {
  itemCode: string;
  modelUid: string;
  model: string;
  /** How many motorcycles were created under this match. */
  unitCount: number;
}

export function fetchUnmappedCodes(): Promise<UnmappedGroup[]> {
  return api<UnmappedGroup[]>('/model-map/unmapped');
}

export function fetchModelOptions(): Promise<ModelOption[]> {
  return api<ModelOption[]>('/model-map/models');
}

export function fetchExistingMappings(): Promise<ExistingMapping[]> {
  return api<ExistingMapping[]>('/model-map');
}

export function mapItemCode(
  itemCode: string,
  modelUid: string,
): Promise<{ mapped: string; unitsCreated: number }> {
  return api('/model-map', { method: 'POST', body: { itemCode, modelUid } });
}

export interface RemapResult {
  itemCode: string;
  from: string;
  to: string;
  unitsChanged: number;
}

export function remapItemCode(input: {
  itemCode: string;
  modelUid: string;
  reason: string;
}): Promise<RemapResult> {
  return api<RemapResult>('/model-map/remap', { method: 'POST', body: input });
}

export function createBikeModel(input: {
  brandName: string;
  model: string;
  variant?: string;
  modelYear?: number;
}): Promise<ModelOption> {
  return api<ModelOption>('/model-map/models', { method: 'POST', body: input });
}

export interface SuggestedCode {
  itemCode: string;
  units: number;
  description: string | null;
}

export interface SuggestedGroup {
  key: string;
  brand: string;
  model: string;
  variant: string | null;
  units: number;
  existingModel: ModelOption | null;
  codes: SuggestedCode[];
}

export interface ModelSuggestions {
  groups: SuggestedGroup[];
  notMotorcycles: SuggestedCode[];
}

export function fetchModelSuggestions(): Promise<ModelSuggestions> {
  return api<ModelSuggestions>('/model-map/suggestions');
}

export function confirmModelGroup(input: {
  itemCodes: string[];
  modelUid?: string;
  brandName?: string;
  model?: string;
  variant?: string;
}): Promise<{ model: string; codes: number; unitsCreated: number }> {
  return api('/model-map/groups', { method: 'POST', body: input });
}

// ─── next service ──────────────────────────────────────────────────────

export interface NextService {
  uid: string;
  status: string;
  ruleCode: string;
  /** Kuala Lumpur calendar date, YYYY-MM-DD. */
  dueDate: string;
  dueMileage: number | null;
}

/** Read from the customer-service module's REST endpoint: features never import each other (CLAUDE.md §4). */
export function getNextService(unitUid: string): Promise<{ nextService: NextService | null }> {
  return api<{ nextService: NextService | null }>(`/service-desk/units/${unitUid}/next-service`);
}

// ─── bike sales to check (spec 2026-10-29) ─────────────────────────────

export interface SaleReview {
  uid: string;
  unitUid: string;
  chassisNo: string;
  bikeItemCode: string | null;
  bikeModel: string;
  lineItemCode: string | null;
  lineDescription: string | null;
  docNo: string;
  /** YYYY-MM-DD */
  soldOn: string;
  debtorCode: string;
}

export function listSaleReviews(page: { cursor?: string }): Promise<CursorPage<SaleReview>> {
  const qs = page.cursor !== undefined ? `?cursor=${encodeURIComponent(page.cursor)}` : '';
  return api<CursorPage<SaleReview>>(`/model-map/sale-reviews${qs}`);
}

export function exportSaleReviewsCsv(): Promise<string> {
  return api<string>('/model-map/sale-reviews?format=csv');
}

export function confirmSaleReview(uid: string): Promise<{ docNo: string; customer: boolean }> {
  return api(`/model-map/sale-reviews/${uid}/confirm`, { method: 'POST' });
}

export function rejectSaleReview(uid: string, reason: string): Promise<void> {
  return api(`/model-map/sale-reviews/${uid}/reject`, { method: 'POST', body: { reason } });
}
